import React from 'react';
import {
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  EnvelopeIcon,
  PhoneIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';

const getInterestStyle = (interest) => {
  switch (interest) {
    case 'VERY_INTERESTED':
      return { label: 'Very Interested', color: 'bg-green-100 text-green-800' };
    case 'INTERESTED':
      return { label: 'Interested', color: 'bg-yellow-100 text-yellow-800' };
    case 'NOT_INTERESTED':
      return { label: 'Not Interested', color: 'bg-red-100 text-red-800' };
    default:
      return { label: 'Undefined', color: 'bg-gray-100 text-gray-600' };
  }
};

const getStatusStyle = (status) => {
  switch (status) {
    case 'PAYING_USER':
      return { label: 'Paying User', color: 'bg-green-100 text-green-800' };
    case 'REQUIRES_ACTION':
      return { label: 'Requires Action', color: 'bg-red-100 text-red-800' };
    case 'WAITING_MEMBER_COMM':
      return { label: 'Waiting', color: 'bg-yellow-100 text-yellow-800' };
    case 'CONTACTED':
      return { label: 'Contacted', color: 'bg-blue-100 text-blue-800' };
    default:
      return { label: 'Not Contacted', color: 'bg-gray-100 text-gray-600' };
  }
};

const AudienceMemberList = ({
  members,
  searchTerm,
  setSearchTerm,
  onEdit,
  onDelete,
  onViewCommunications,
  page,
  setPage,
  totalMembers,
  pageSize = 10,
}) => {
  // Note: Filtering is done server-side via the query parameter
  // The members prop already contains filtered results

  return (
    <div>
      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
            placeholder="Search members by name, email, phone, or CUI..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Table */}
      <div className="flex flex-col">
        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Member
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Current Software
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Interest
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      CRM Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Follow-up
                    </th>
                    <th className="relative px-6 py-3">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {members.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                        No audience members found. Add your first member to get started.
                      </td>
                    </tr>
                  ) : (
                    members.map((member) => {
                      const info = member.memberInfo || {};
                      const memberType = member.type || 'AUTO_SHOP';
                      const typeLabel = memberType === 'ITP_STATION' ? 'ITP Station' : 'Auto Shop';
                      const typeColor = memberType === 'ITP_STATION' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800';
                      const interestStyle = getInterestStyle(member.interest);
                      const statusStyle = getStatusStyle(member.status);

                      return (
                        <tr key={member.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="h-10 w-10 flex-shrink-0">
                                <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                                  <span className="text-purple-600 font-medium text-sm">
                                    {info.name?.charAt(0)?.toUpperCase() || '?'}
                                  </span>
                                </div>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {info.name || 'Unknown'}
                                </div>
                                {info.cui && (
                                  <div className="text-xs text-gray-400">
                                    CUI: {info.cui}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex flex-col gap-1">
                              {info.email && (
                                <div className="flex items-center text-sm text-gray-500">
                                  <EnvelopeIcon className="h-4 w-4 mr-1" />
                                  {info.email}
                                </div>
                              )}
                              {info.phone && (
                                <div className="flex items-center text-sm text-gray-500">
                                  <PhoneIcon className="h-4 w-4 mr-1" />
                                  {info.phone}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${typeColor}`}>
                              {typeLabel}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {member.currentSoft || <span className="text-gray-400">N/A</span>}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${interestStyle.color}`}>
                              {interestStyle.label}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusStyle.color}`}>
                              {statusStyle.label}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {member.nextFollowUpDate ? (
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                new Date(member.nextFollowUpDate) < new Date() 
                                  ? 'bg-red-100 text-red-800' 
                                  : 'bg-blue-100 text-blue-800'
                              }`}>
                                {new Date(member.nextFollowUpDate).toLocaleDateString('ro-RO', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric'
                                })}
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              onClick={() => onViewCommunications(member)}
                              className="text-blue-600 hover:text-blue-900 mr-3"
                              title="CRM Communications"
                            >
                              <ChatBubbleLeftRightIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => onEdit(member)}
                              className="text-purple-600 hover:text-purple-900 mr-3"
                              title="Edit"
                            >
                              <PencilIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => onDelete(member.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Pagination */}
      {members.length > 0 && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-gray-700">
            Showing <span className="font-medium">{members.length}</span> of{' '}
            <span className="font-medium">{totalMembers || members.length}</span> members
            {totalMembers > 0 && ` (Page ${page})`}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(page + 1)}
              disabled={totalMembers && page * pageSize >= totalMembers}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AudienceMemberList;

